import {
  Component,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ButtonDirective,
  LoaderComponent,
  ModalComponent as NdwModalComponent,
  ModalContentComponent,
  ModalFooterComponent,
  ModalHeaderComponent,
  ModalService,
  ModalSize,
} from '@ndwnu/design-system';
import { CommonModule, NgStyle } from '@angular/common';

@Component({
  selector: 'app-modal',
  templateUrl: 'custom-modal.component.html',
  styleUrl: 'custom-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ModalHeaderComponent,
    ModalContentComponent,
    ModalFooterComponent,
    NdwModalComponent,
    NgStyle,
    CommonModule,
    ButtonDirective,
    LoaderComponent,
  ],
})
export class CustomModalComponent implements OnDestroy {
  readonly width = input<string>('');
  readonly maxWidth = input<string | undefined>(undefined);
  readonly minHeight = input<string | undefined>(undefined);
  readonly title = input<string>('');
  readonly size = input<ModalSize>('sm');
  readonly noScroll = input(false);
  readonly confirmText = input<string>('');
  readonly customClasses = input<string>('');
  readonly confirmDisabled = input<boolean>(false);
  // Closing the modal after that is the responsibility of the parent component.
  readonly spinnerOnConfirm = input<boolean>(false);
  confirmed = output<void>();
  // This will add a spinner to the confirmation button when it is clicked.
  closed = output<void>();
  readonly templateRef = viewChild.required<TemplateRef<unknown>>('template');
  readonly isOpen = signal(false);
  readonly isSubmitting = signal(false);
  private readonly modalService = inject(ModalService);
  private readonly viewContainer = inject(ViewContainerRef);
  // The ModalService only tracks the last opened modal; keep our own dialog ref so
  // close() still closes this modal after a nested modal (e.g. a confirmation) was opened.
  private dialogRef?: ReturnType<ModalService['open']>;

  get maxWidthToUse() {
    return this.maxWidth() ?? this.width();
  }

  open() {
    this.dialogRef = this.modalService.open(this.templateRef(), this.viewContainer);

    this.dialogRef?.closed.subscribe(() => {
      if (this.isOpen()) {
        this.isOpen.set(false);
        this.closed.emit();
      }
    });
    this.isOpen.set(true);
  }

  confirm() {
    this.confirmed.emit();
    if (this.spinnerOnConfirm()) {
      this.isSubmitting.set(true);
    } else {
      this.close();
    }
  }

  close() {
    if (this.isOpen()) {
      this.isOpen.set(false);
      this.closed.emit();
      this.dialogRef?.close();
    }
  }

  // Clears the spinner state when spinnerOnConfirm is used and the parent's
  // confirm handling fails, so the confirm button becomes usable again.
  resetSubmitting() {
    this.isSubmitting.set(false);
  }

  ngOnDestroy() {
    this.dialogRef?.close();
  }
}
